/**
 * Database Source inspector: pick a connection, then a schema and table — or
 * write a SELECT — and Test to preview the rows the run will read.
 *
 * **Test is a button, not an effect.** Blob Source auto-loads its columns as
 * soon as it is configured, which is free there; here every read of rows is an
 * audited access event against a customer's database (`database.rows_read`),
 * and writing one of those each time someone clicks through a schema picker
 * would fill the trail with reads nobody asked for. The columns a Test finds
 * are persisted on the node (`previewColumns`), so downstream pickers stay
 * populated afterwards and across a reload.
 *
 * The preview calls the **same endpoint the run calls**, with a smaller cap —
 * so what you see is what a run will read, not an approximation of it.
 */

import { useCallback, useEffect, useState } from "react";
import { Alert, Button, SegmentedControl, Select, Stack, Text, Textarea } from "@mantine/core";
import { IconLock, IconPlayerPlay } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { listConnections } from "@/shared/api/integration";
import {
  databaseRead,
  databaseSchemas,
  databaseTables,
  type DatabaseRows,
} from "@/shared/api/database";
import { DatabaseRowsTable } from "@/features/integration/DatabaseBrowser";
import { toastApiError } from "@/shared/ui/toast";

import { ConnectionRefSelect } from "./ConnectionRefSelect";
import { useConnectionRef } from "./useConnectionRef";

/** Rows fetched by a Test. Deliberately smaller than a run's cap (10,000) —
 *  this is a look at the data, not the extract. */
const PREVIEW_ROWS = 100;

export function DatabaseSourceConfig({
  config,
  editable,
  onChange,
  onColumns,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  onColumns?: (cols: string[]) => void;
}) {
  const getToken = useAccessToken();
  // A node names a connection id or a `@role`; everything here takes an id.
  const {
    connectionId,
    role,
    settled: roleSettled,
  } = useConnectionRef(String(config.connectionId ?? ""));
  const mode = String(config.mode ?? "table");
  const schema = String(config.schema ?? "");
  const table = String(config.table ?? "");
  const query = String(config.query ?? "");

  const [connections, setConnections] = useState<{ value: string; label: string }[]>([]);
  const [schemas, setSchemas] = useState<string[]>([]);
  const [tables, setTables] = useState<string[]>([]);
  const [testing, setTesting] = useState(false);
  const [preview, setPreview] = useState<DatabaseRows | null>(null);

  // The tenant's database connections, once.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const items = (await listConnections(token, "database")).items;
        if (!cancelled)
          setConnections(items.map((c) => ({ value: c.connection_id, label: c.display_name })));
      } catch (err) {
        toastApiError("Could not load connections", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  // Schemas when a connection is chosen — metadata, not an audited read.
  useEffect(() => {
    if (!connectionId) {
      setSchemas([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const items = (await databaseSchemas(token, connectionId)).items;
        if (!cancelled) setSchemas(items.map((s) => s.name));
      } catch (err) {
        toastApiError("Could not list schemas", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId]);

  useEffect(() => {
    if (!connectionId || !schema) {
      setTables([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const items = (await databaseTables(token, connectionId, schema)).items;
        if (!cancelled) setTables(items.map((t) => t.name));
      } catch (err) {
        toastApiError("Could not list tables", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId, schema]);

  const ready = Boolean(connectionId) && (mode === "query" ? query.trim() !== "" : Boolean(table));

  const onTest = useCallback(async () => {
    if (!ready) return;
    setTesting(true);
    try {
      const token = await getToken();
      const result = await databaseRead(token, connectionId, {
        ...(mode === "query" ? { query } : { schema, table }),
        limit: PREVIEW_ROWS,
      });
      setPreview(result);
      onColumns?.(result.columns); // immediate (this session)
      // Persisted → survives a reload and feeds the downstream pickers without
      // a second read of the customer's database.
      onChange("previewColumns", result.columns);
    } catch (err) {
      toastApiError("Test failed", err);
    } finally {
      setTesting(false);
    }
  }, [ready, getToken, connectionId, mode, query, schema, table, onChange, onColumns]);

  return (
    <Stack gap="xs">
      <ConnectionRefSelect
        label="Database connection"
        placeholder={connections.length ? "Pick a connection" : "No database connections set up"}
        data={connections}
        nothingFoundMessage="No database connections"
        connectionId={connectionId}
        role={role}
        settled={roleSettled}
        editable={editable}
        onChange={(v) => {
          const name = connections.find((c) => c.value === v)?.label ?? "";
          onChange("connectionId", v);
          onChange("connectionName", name);
          onChange("schema", "");
          onChange("table", "");
          setPreview(null);
        }}
      />

      {connectionId && (
        <>
          <SegmentedControl
            fullWidth
            size="xs"
            value={mode}
            disabled={!editable}
            onChange={(v) => {
              onChange("mode", v);
              setPreview(null);
            }}
            data={[
              { value: "table", label: "A table" },
              { value: "query", label: "A SELECT query" },
            ]}
          />

          {mode === "table" ? (
            <>
              <Select
                label="Schema"
                placeholder={schemas.length ? "Pick a schema" : "No schemas visible"}
                data={schemas}
                value={schema || null}
                disabled={!editable}
                searchable
                onChange={(v) => {
                  onChange("schema", v ?? "");
                  onChange("table", "");
                  setPreview(null);
                }}
              />
              <Select
                label="Table or view"
                placeholder={schema ? "Pick a table" : "Pick a schema first"}
                data={tables}
                value={table || null}
                disabled={!editable || !schema}
                searchable
                onChange={(v) => {
                  onChange("table", v ?? "");
                  setPreview(null);
                }}
              />
            </>
          ) : (
            <Textarea
              label="SELECT query"
              description="One statement, starting with SELECT or WITH. Anything else is refused."
              placeholder="SELECT id, name FROM schema.table WHERE ..."
              autosize
              minRows={3}
              value={query}
              disabled={!editable}
              onChange={(e) => {
                onChange("query", e.currentTarget.value);
                setPreview(null);
              }}
            />
          )}

          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlayerPlay size={14} />}
            onClick={() => void onTest()}
            loading={testing}
            disabled={!ready}
          >
            Test — preview data
          </Button>

          <Alert variant="light" icon={<IconLock size={16} />} p="xs">
            <Text size="xs">
              Reads only — a run and this preview both refuse anything that is not a single SELECT.
              A run reads up to 10,000 rows and says so when it stops there; this preview shows the
              first {PREVIEW_ROWS}.
            </Text>
          </Alert>
        </>
      )}

      {preview && (
        <Stack gap={4}>
          {preview.truncated && (
            <Text size="xs" c="yellow.7">
              More rows than the preview shows — the first {preview.max_rows} are below.
            </Text>
          )}
          <DatabaseRowsTable rows={preview.rows} columns={preview.columns} max={20} />
        </Stack>
      )}
    </Stack>
  );
}
